import type { ReactElement } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Button, Dialog, Inline, Stack, breakpoints, Text, textVariants, vars } from '@etg/wings';
import { css, cx, etiColors } from '@eti/styles';
import { useProperty, useSearch, useTranslation } from '@eti/providers';
import { useHistory } from 'react-router-dom';
import { PlusCircle } from '@phosphor-icons/react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { LoadingDots } from '@eti/components';
import { flexibleTicket, cancelForAnyReason } from '@eti/products';
import type {
  FlightsFragment,
  GetBookingStepPageDataQuery,
  GetSelectedTripOnTravelerDetailsPageQuery,
  QueryRootSearchArgs,
} from '@eti/schema-types';
import { constants } from '@eti/utils';
import { getUniqueMarketingCarriersFromSegments } from '../../../../utils/carriers';
import { useToggle } from '../../../../common/custom-hooks/toggle';
import { routes } from '../../../../constants/routesAndApisConstants';
import { getTravelersFromFormValues } from '../../../../utils/search';
import { getChildrenAges, transformPassengers } from '../../../result/utils/searchDetails';
import { SEARCH_QUERY } from '../../../result/graphql/queries';
import type {
  TransformedBookingStepCountryType,
  TransformedBookingStepTravelerType,
} from '../../../../common/types/transformations/models';
import { SELECT_TRIP_WITH_DATA } from '../../../result/graphql/mutations';
import { TRAVELER_DETAILS_QUERY } from '../../graphql/queries';
import {
  getCFARTicketConfig,
  getFlexibleTicketConfig,
  isCFARTicketAllowedForCarriers,
  isFlexibleTicketAllowedForCarriers,
} from '../../../result/utils/resultUtils';
import { BOOKING_STEP_QUERY } from '../../../booking-step/graphql/queries';
import { usePassengerOptions } from '../../utils/edit-passengers/Passengers';
import { transformTravelersData } from '../../../../common/utils/utils';
import { transformCountry } from '../../utils/formatUtils';
import type {
  TravelerDetailsQueryBoundsType,
  TravelerDetailsQueryTravelerPricesType,
} from '../../../../common/types/queries/getSelectedTripOnTravelerDetailsPageQuery/models';
import type { BookingStepQueryBoundsType } from '../../../../common/types/queries/getBookingStepPageDataQuery/models';

const optionStyles = css`
  &.optionHeaderStyles {
    background-color: ${etiColors.grayScale[4]};
    border-top: 1px solid ${etiColors.grayScale[8]};
    color: ${etiColors.grayScale[10]};
  }

  > strong {
    display: block;
    margin-block-end: 16px;
  }
`;

const optionsInDialog = css`
  border-bottom: 1px solid ${vars.colors.divider};
  padding-bottom: 16px;
`;
const childInfoLabel = css`
  background-color: ${vars.colors.pageBackdrop};
  margin-top: -16px;
  padding: 8px;
`;

const childAgeLabel = css`
  padding-top: 16px;
`;

interface PassengersOptionDetailsProps {
  dataTestId: string;
  label: ReactElement;
  className: string;
}

interface PassengersOptionsProps {
  label: string;
  options: PassengersOptionDetailsProps[];
}

export const oneHourIntoMilliSeconds = 3600000;

function getDepartureMinutesInMilliSeconds(departureMinutes: number) {
  if (departureMinutes > 30) {
    return oneHourIntoMilliSeconds / 2;
  }
  return 0;
}

interface EditPassengersProps {
  constraints:
    | {
        isDisabled: boolean;
        maxNumberOfTravelers: number;
      }
    | undefined;
  updateTravelerPricesOnEditPassenger: (
    newTravelersPrice: TravelerDetailsQueryTravelerPricesType,
    newTravelers: TransformedBookingStepTravelerType[],
  ) => void;
  selectedTripBounds?: TravelerDetailsQueryBoundsType | BookingStepQueryBoundsType;
  selectionKey: string;
  tripId: string;
}

const EditPassengers = ({
  constraints = { isDisabled: false, maxNumberOfTravelers: 0 },
  updateTravelerPricesOnEditPassenger,
  selectedTripBounds,
  selectionKey,
  tripId,
}: EditPassengersProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const isDynamicBookingFlowEnabled = p('TravelerDetails.DynamicFlow.Enabled');
  const { SEARCH_RESULT_FLEX_BEHAVIOR_OPTIONS } = constants;
  const isRedirectToTDEnabled = p('IbeClient.TravelerDetails.EditPassenger.RedirectToTD.Enabled');
  const { isOpen, handleClose, handleOpen } = useToggle(false);
  const history = useHistory();
  const { currentSearchData, setSearchData, searchVariables } = useSearch();
  const [passengers, setPassengers] = useState(() =>
    transformPassengers(currentSearchData?.travelers, false),
  );
  const [isLoading, setIsLoading] = useState(false);

  const [shouldMakeNewQuery, setShouldMakeNewQuery] = useState(false);
  const options = usePassengerOptions(constraints, passengers, setPassengers);
  const travelerTexts = useMemo(() => {
    return {
      adult: t('Adult'),
      adults: t('Adults'),
      child: t('Child'),
      children: t('Children'),
      infant: t('Infant'),
      infants: t('Infants'),
    };
  }, [t]);

  const handleRedirectToTDPage = () => {
    const newTravelers = getTravelersFromFormValues(
      transformPassengers(passengers, true),
      travelerTexts,
    );
    if (setSearchData) {
      setSearchData({
        travelers: newTravelers,
      });
    }
    history.push(routes.RESULT);
  };

  const onEditPassengerSearch = (
    queryData: GetSelectedTripOnTravelerDetailsPageQuery | GetBookingStepPageDataQuery,
  ) => {
    const { selectedTrip, travelerDetails, countries } = queryData;
    const transformedCountries: TransformedBookingStepCountryType[] =
      countries?.map((country) => transformCountry(country)) || [];
    const newTravelers = transformTravelersData(
      selectedTrip,
      travelerDetails,
      transformedCountries,
    );
    updateTravelerPricesOnEditPassenger(selectedTrip?.travelerPrices ?? [], newTravelers);
  };

  const onAPISearchComplete = (
    queryData: GetBookingStepPageDataQuery | GetSelectedTripOnTravelerDetailsPageQuery | null,
  ) => {
    if (queryData) {
      onEditPassengerSearch(queryData);
      setIsLoading(false);
      handleClose();
      history.go(0);
    } else {
      handleRedirectToTDPage();
    }
  };

  const selectedQuery = isDynamicBookingFlowEnabled ? BOOKING_STEP_QUERY : TRAVELER_DETAILS_QUERY;

  const [getTravelerDetails] = useLazyQuery(selectedQuery, {
    variables: {
      stepNumber: 0,
    },
    fetchPolicy: 'no-cache',
  });

  const [selectTripWithData] = useMutation(SELECT_TRIP_WITH_DATA, {
    onCompleted: ({ selectTrip: selectedTripResponse }) => {
      if (selectedTripResponse.success) {
        getTravelerDetails({
          onCompleted: (travelerQueryData) => {
            onAPISearchComplete(travelerQueryData);
          },
          onError: () => {
            handleRedirectToTDPage();
          },
        });
      }
    },
    onError: () => {
      handleRedirectToTDPage();
    },
  });

  const marketingCarriers =
    selectedTripBounds &&
    selectedTripBounds.reduce(
      (acc, bound) => {
        if (bound && bound.segments) {
          const { segments } = bound;
          const carriers = getUniqueMarketingCarriersFromSegments(segments);
          acc.push(...carriers);
          return acc;
        }
        return acc;
      },
      [] as { code: string; value: string }[],
    );

  const allMarketingCarriersCode = marketingCarriers && marketingCarriers.map((c) => c.code);

  const firstSegment =
    (selectedTripBounds &&
      selectedTripBounds[0]?.segments[0]?.__typename === 'TripSegment' &&
      selectedTripBounds[0].segments[0]) ||
    null;
  const departureTime = firstSegment && firstSegment.departuredAt;

  const departureTimeSplit = departureTime && departureTime.split('T')[1].split(':');
  const departureTimeHour = departureTimeSplit && departureTimeSplit[0];
  const departureTimeMinutes = departureTimeSplit && departureTimeSplit[1];

  const departureHourInMilliSeconds = Number(departureTimeHour as string) * oneHourIntoMilliSeconds;
  const departureMinInMilliSeconds =
    departureTimeMinutes && getDepartureMinutesInMilliSeconds(Number(departureTimeMinutes));

  const filteredDuration = departureHourInMilliSeconds + (departureMinInMilliSeconds as number);
  const updatedChildrenAge = passengers.children
    ? getChildrenAges(passengers.children, passengers.childrenAges)
    : [];
  const updatedInfantAge = passengers.infants
    ? getChildrenAges(passengers.infants, passengers.infantsAges)
    : [];

  const newSearch: QueryRootSearchArgs = {
    ...(searchVariables as QueryRootSearchArgs),
    adults: passengers.adults as number,
    childAges: [...updatedChildrenAge, ...updatedInfantAge],
    filters: [
      {
        code: 'TRIP_0_DEPARTURE_TIME',
        value: `${filteredDuration},${filteredDuration + oneHourIntoMilliSeconds / 2}`,
      },
      {
        code: 'AIRLINES',
        value: allMarketingCarriersCode?.join(','),
      },
    ],
  };

  const [getSearchData] = useLazyQuery(SEARCH_QUERY, {
    onCompleted: (responseData) => {
      const flights = responseData.search?.flights;
      const matchingFlight = flights?.find(
        (flight) =>
          (flight as FlightsFragment).tripId === tripId ||
          (flight as FlightsFragment).selectionKey === selectionKey,
      );
      if (matchingFlight) {
        const flexibleTicketAvailability =
          isFlexibleTicketAllowedForCarriers(
            selectedTripBounds,
            p('Result.HideFlexTicket.Carriers'),
          ) &&
          p('IbeClient.SearchResult.Flex.Behaviour') ===
            SEARCH_RESULT_FLEX_BEHAVIOR_OPTIONS.AIR_REBOOKING
            ? getFlexibleTicketConfig(
                (matchingFlight as FlightsFragment).availableExtraProducts as any,
              )
            : null;

        const isFlexibleTicket = flexibleTicketAvailability?.sellSpecification?.sellPriceTravelers;

        const isCfarTicket =
          isCFARTicketAllowedForCarriers(selectedTripBounds, p('Result.HideCFAR.Carriers')) &&
          p('IbeClient.SearchResult.Flex.Behaviour') ===
            SEARCH_RESULT_FLEX_BEHAVIOR_OPTIONS.CANCEL_FOR_ANY_REASON
            ? getCFARTicketConfig((matchingFlight as FlightsFragment).availableExtraProducts as any)
            : null;
        selectTripWithData({
          variables: {
            selectionKey,
            ...(isFlexibleTicket && { extraProductSelection: [flexibleTicket.id] }),
            ...(isCfarTicket && { extraProductSelection: [cancelForAnyReason.id] }),
          },
        });
      } else {
        onAPISearchComplete(null);
      }
    },
    onError: () => {
      handleRedirectToTDPage();
    },
  });

  useEffect(() => {
    if (shouldMakeNewQuery) {
      getSearchData({
        fetchPolicy: 'no-cache',
        variables: newSearch,
      });
    }
    setShouldMakeNewQuery(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldMakeNewQuery]);

  const handleUpdateSearch = () => {
    if (isRedirectToTDEnabled) {
      setIsLoading(true);
      setShouldMakeNewQuery(true);
    } else {
      handleRedirectToTDPage();
    }
  };

  return (
    <>
      <Inline align="center" data-testid="travelerDetails-editPassengers-wrapper">
        <Button
          data-testid="travelerDetails-editPassengers-button"
          iconPrefix={<PlusCircle />}
          onClick={handleOpen}
        >
          {t('TravelerDetails.EditPassengers.Button.Label')}
        </Button>
      </Inline>
      <Dialog
        closeButtonAriaLabel={t('ScreenReader.CloseButtonDialog.AriaLabel.Text')}
        isOpen={isOpen}
        onDismiss={handleClose}
        title={t('TravelerDetails.EditPassengers.Header')}
      >
        <Stack spacing={24}>
          <div>{t('TravelerDetails.EditPassengers.Content')}</div>
          <Stack data-testid="travelerDetails-editPassengers-mainContent" spacing={16}>
            {options
              .filter((option) => Boolean(option.label))
              .map((mappedOption) => {
                const isNestedOptionPresent = (mappedOption as unknown as PassengersOptionsProps)
                  ?.options;
                return (
                  <div
                    className={cx(
                      optionStyles,
                      (mappedOption as unknown as PassengersOptionDetailsProps)?.className,
                    )}
                    data-testid={
                      (mappedOption as unknown as PassengersOptionDetailsProps)?.dataTestId
                    }
                    key={
                      (mappedOption as unknown as PassengersOptionDetailsProps).label?.props
                        ?.title ?? mappedOption.label
                    }
                  >
                    {typeof mappedOption.label === 'string' ? (
                      <Stack className={childInfoLabel}>
                        <Text as="p" style={{ color: '#737373' }} variant={textVariants._14}>
                          <strong>{mappedOption.label}</strong>
                        </Text>
                      </Stack>
                    ) : (
                      mappedOption.label
                    )}
                    <Stack className={optionsInDialog} spacing={16}>
                      {isNestedOptionPresent &&
                        (mappedOption as unknown as PassengersOptionsProps).options.map(
                          (mOption, index) => (
                            <span className={childAgeLabel} key={`nestedDialogOption-${index}`}>
                              <div
                                className={cx(optionStyles, mOption?.className)}
                                data-testid={mOption.dataTestId}
                                key={mOption.label?.props?.title ?? mappedOption.label}
                              >
                                {mOption.label}
                              </div>
                            </span>
                          ),
                        )}
                    </Stack>
                  </div>
                );
              })}
          </Stack>
          <Inline align="end" collapseBelow={breakpoints._768} spacing={16}>
            <Button
              data-testid="travelerDetails-editPassengers-closeModal-button"
              disabled={isLoading}
              onClick={handleClose}
            >
              {t('TravelerDetails.EditPassengers.CloseButton.Label')}
            </Button>
            <Button
              data-testid="travelerDetails-editPassengers-updateSearch-button"
              onClick={handleUpdateSearch}
              variant="primary"
            >
              {isLoading ? (
                <LoadingDots />
              ) : (
                t('TravelerDetails.EditPassengers.UpdateSearchButton.Label')
              )}
            </Button>
          </Inline>
        </Stack>
      </Dialog>
    </>
  );
};

export default EditPassengers;
